import { fetchApi } from "~/lib/api";
import type {
  AdminCreateNewsletterCampaignRequest,
  AdminGetNewsletterCampaignsParams,
  AdminGetNewsletterSubscriptionsParams,
  AdminNewsletterStatistics,
  EmailTemplateMeta,
  NewsletterCampaign,
  NewsletterStatusResponse,
  NewsletterSubscription,
  NewsletterSubscriptionResponse,
  NewsletterUnsubscribeResponse,
  Pagination,
} from "~/types";
import { serializeFilterParams } from "./utils";

/**
 * Customer newsletter service - for customer newsletter subscriptions
 */
export const customerNewsletterService = {
  /**
   * Subscribe to newsletter with email
   */
  async subscribe(
    email: string,
    sourceUrl?: string,
    sourceEvent?: string,
  ): Promise<NewsletterSubscriptionResponse> {
    return fetchApi<NewsletterSubscriptionResponse>(
      "/anonymous/newsletter/subscribe",
      {
        method: "POST",
        body: JSON.stringify({ email, sourceUrl, sourceEvent }),
      },
    );
  },

  /**
   * Get current user's newsletter subscription status
   */
  async getStatus(): Promise<NewsletterStatusResponse> {
    return fetchApi<NewsletterStatusResponse>("/anonymous/newsletter/status");
  },

  /**
   * Unsubscribe from newsletter by email (for authenticated users)
   */
  async unsubscribeByEmail(
    email: string,
  ): Promise<NewsletterUnsubscribeResponse> {
    return fetchApi<NewsletterUnsubscribeResponse>(
      "/anonymous/newsletter/unsubscribe",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
  },
};

export const adminNewsletterService = {
  /**
   * Get newsletter subscriptions with pagination and filtering
   */
  async getSubscriptions(
    params: AdminGetNewsletterSubscriptionsParams = {},
  ): Promise<Pagination<NewsletterSubscription>> {
    const query = serializeFilterParams(params);

    const endpoint = `/admin/newsletter/subscriptions${query}`;
    return fetchApi<Pagination<NewsletterSubscription>>(endpoint);
  },

  /**
   * Get newsletter campaigns with pagination
   */
  async getCampaigns(
    options: AdminGetNewsletterCampaignsParams = {},
  ): Promise<Pagination<NewsletterCampaign>> {
    const query = serializeFilterParams(options);

    const endpoint = `/admin/newsletter/campaigns${query}`;
    return fetchApi<Pagination<NewsletterCampaign>>(endpoint);
  },

  /**
   * Get newsletter statistics
   */
  async getStats(): Promise<AdminNewsletterStatistics> {
    return fetchApi<AdminNewsletterStatistics>("/admin/newsletter/stats");
  },

  /**
   * Get newsletter templates (templates whose name starts with 'newsletter')
   */
  async getNewsletterTemplates(): Promise<EmailTemplateMeta[]> {
    return fetchApi<EmailTemplateMeta[]>("/admin/newsletter/templates");
  },

  /**
   * Get a single newsletter campaign by ID
   */
  async getCampaign(id: number): Promise<NewsletterCampaign> {
    return fetchApi<NewsletterCampaign>(`/admin/newsletter/campaigns/${id}`);
  },

  /**
   * Create a new newsletter campaign
   */
  async createCampaign(data: AdminCreateNewsletterCampaignRequest): Promise<NewsletterCampaign> {
    return fetchApi<NewsletterCampaign>("/admin/newsletter/campaigns", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a newsletter campaign
   */
  async updateCampaign(
    id: number,
    data: AdminCreateNewsletterCampaignRequest,
  ): Promise<NewsletterCampaign> {
    return fetchApi<NewsletterCampaign>(`/admin/newsletter/campaigns/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Send a newsletter campaign
   */
  async sendCampaign(id: number): Promise<{ message: string }> {
    return fetchApi<{ message: string }>(
      `/admin/newsletter/campaigns/${id}/send`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Delete a newsletter campaign
   */
  async deleteCampaign(id: number): Promise<{ message: string }> {
    return fetchApi<{ message: string }>(`/admin/newsletter/campaigns/${id}`, {
      method: "DELETE",
    });
  },
};
